import { memo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { getNameWithoutExtension } from '../../../../../utils/phone-utils';
import {
  hasCallGroupExpansion,
  hasCallQueueExpansion,
  hasDeviceExpansion,
  hasForwardingNumberExpansion,
  hasPhoneHoursExpansion,
  hasPhoneTreeExpansion,
  hasPlayMessageExpansion,
  hasVoicemailExpansion,
  SharedNodeProps,
} from '../data';
import { BaseRoutingNode } from './base-routing-node';

export const CallGroupNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasCallGroupExpansion(data.callObject) ? data.callObject : undefined;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='call-group'
        title='Call Group'
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Caller Label')} label={callObject?.callGroupExpansion.callerLabel || '--'} />

          <DevicesLabelItem deviceNames={callObject?.callGroupExpansion.deviceNames ?? []} />
        </div>
      </BaseRoutingNode>
    );
  }
);
CallGroupNode.displayName = 'CallGroupNode';

export const CallQueueNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');

    const callObject = hasCallQueueExpansion(data.callObject) ? data.callObject : undefined;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='call-queue'
        title='Call Queue'
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing(1)};
            `}
          >
            <LabelItem title={t('Caller Label')} label={callObject?.callQueueExpansion.callerLabel || '--'} />

            <DevicesLabelItem deviceNames={callObject?.callQueueExpansion.deviceNames ?? []} />

            <LabelItem
              title={t('Greeting')}
              label={getNameWithoutExtension(callObject?.callQueueExpansion.greetingMediaFileName) || '--'}
            />

            {AudioPlayerComponent &&
              callObject?.callQueueExpansion.greetingMediaItemId &&
              callObject?.callQueueExpansion.downloadUrl && (
                <AudioPlayerComponent mediaItemId={callObject?.callQueueExpansion.greetingMediaItemId} />
              )}
          </div>
        </>
      </BaseRoutingNode>
    );
  }
);
CallQueueNode.displayName = 'CallQueueNode';

export const CallRouteNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='call-route'
        title='Call Route'
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      ></BaseRoutingNode>
    );
  }
);
CallRouteNode.displayName = 'CallRouteNode';

export const ForwardingDeviceNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');

    const callObject = hasDeviceExpansion(data.callObject) ? data.callObject : undefined;

    const ringDuration = (callObject?.deviceExpansion.timeout ?? 0) / 1000;
    const ringDurationText = ringDuration ? `${t('{{duration}} seconds', { duration: ringDuration })}` : '--';

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='cell-phone'
        title='Device'
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Ring Duration')} label={ringDurationText} />

          <LabelItem title={t('Caller Label')} label={callObject?.deviceExpansion.callerLabel || '--'} />
        </div>
      </BaseRoutingNode>
    );
  }
);
ForwardingDeviceNode.displayName = 'ForwardingDeviceNode';

export const ForwardingNumberNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    const { t } = useTranslation('phone');

    const callObject = hasForwardingNumberExpansion(data.callObject) ? data.callObject : undefined;

    const ringDuration = (callObject?.forwardingNumberExpansion?.timeout ?? 0) / 1000;
    const ringDurationText = ringDuration
      ? `${t('{{duration}} seconds', { duration: ringDuration })}`
      : t('Ring until answered');

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='send-forward'
        title='Forwarding Number'
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <LabelItem title={t('Ring Duration')} label={ringDurationText} />
      </BaseRoutingNode>
    );
  }
);
ForwardingNumberNode.displayName = 'ForwardingNumberNode';

export const OfficeHoursNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    DayHoursListComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const callObject = hasPhoneHoursExpansion(data.callObject) ? data.callObject : undefined;

    const phoneHours = callObject?.phoneHoursExpansion?.phoneHours ?? [];

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='clock'
        title='Phone Hours'
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        {DayHoursListComponent && !!phoneHours.length && (
          <div
            // Need to apply the "nowheel" to allow scrolling inside the custom node
            // From react-flow docs: "If you have scroll containers inside your custom node
            // you can add the class nowheel to disable the default canvas pan behaviour when
            // scrolling inside your custom nodes."
            className='nowheel'
            css={css`
              display: flex;
              flex-direction: column;
              gap: 8px;
              height: 316px;
              overflow-y: scroll;
            `}
          >
            <DayHoursListComponent phoneHours={phoneHours} />
          </div>
        )}
      </BaseRoutingNode>
    );
  }
);
OfficeHoursNode.displayName = 'OfficeHoursNode';

export const PhoneTreeNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasPhoneTreeExpansion(data.callObject) ? data.callObject : undefined;
    const { timeout, maxFailures, maxTimeouts, promptMediaFileName, promptMediaItemId } =
      callObject?.phoneTreeExpansion || {};

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='phone-tree'
        title='Phone Tree'
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem title={t('Phone Tree Prompt')} label={promptMediaFileName || '--'} />

          {AudioPlayerComponent && promptMediaItemId ? (
            <AudioPlayerComponent mediaItemId={promptMediaItemId} />
          ) : undefined}

          <LabelItem title={t('Timeout After')} label={((timeout ?? 0) / 1000).toFixed(0) + ' seconds'} />

          <LabelItem title={t('Max Timeouts')} label={maxTimeouts || '--'} />

          <LabelItem title={t('Max Invalid Selections')} label={maxFailures || '--'} />
        </div>
      </BaseRoutingNode>
    );
  }
);
PhoneTreeNode.displayName = 'PhoneTreeNode';

export const PlayNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const callObject = hasPlayMessageExpansion(data.callObject) ? data.callObject : undefined;
    const mediaItemId = callObject?.playMessageExpansion?.mediaItemId;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='play'
        title='Play Message'
        label={getNameWithoutExtension(data.label)}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        {AudioPlayerComponent && mediaItemId ? <AudioPlayerComponent mediaItemId={mediaItemId} /> : undefined}
      </BaseRoutingNode>
    );
  }
);
PlayNode.displayName = 'PlayNode';

export const VoicemailNode = memo(
  ({
    data,
    type,
    canEdit,
    onCallObjectLinkClick,
    AudioPlayerComponent,
    onDeleteIconClick,
    onEditIconClick,
  }: SharedNodeProps) => {
    const { t } = useTranslation('phone');
    const callObject = hasVoicemailExpansion(data.callObject) ? data.callObject : undefined;
    const greetingId = callObject?.voicemailExpansion?.greetingMediaItemId;
    const downloadUrl = callObject?.voicemailExpansion?.downloadUrl;

    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='voicemail'
        title={'Voicemail'}
        label={data.label}
        warning={data.warning}
        linkId={data.callObject?.primitiveId}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <LabelItem
            title={t('Greeting')}
            label={getNameWithoutExtension(callObject?.voicemailExpansion?.greetingMediaFileName) || '--'}
          />
          {AudioPlayerComponent && greetingId && downloadUrl && (
            <AudioPlayerComponent mediaItemId={greetingId} downloadUrl={downloadUrl} />
          )}
        </div>
      </BaseRoutingNode>
    );
  }
);
VoicemailNode.displayName = 'VoicemailNode';

export const RepeatNode = memo(
  ({ data, type, canEdit, onCallObjectLinkClick, onDeleteIconClick, onEditIconClick }: SharedNodeProps) => {
    return (
      <BaseRoutingNode
        nodeId={data.id}
        icon='automated-message'
        title={'Repeat Phone Tree'}
        label={data.label}
        warning={data.warning}
        type={type}
        canEdit={canEdit}
        onClickDelete={onDeleteIconClick}
        onClickEdit={onEditIconClick}
        onCallObjectLinkClick={() =>
          onCallObjectLinkClick?.({ callObjectId: data.callObject?.primitiveId ?? '', nodeId: data.id, type })
        }
      />
    );
  }
);
RepeatNode.displayName = 'RepeatNode';

const LabelItem = ({ title, label }: { title: string; label?: string | number }) => {
  return (
    <div>
      <Text size='small' color='subdued'>
        {title}
      </Text>
      <Text size='medium'>{label}</Text>
    </div>
  );
};

const DevicesLabelItem = ({ deviceNames }: { deviceNames: string[] }) => {
  const { t } = useTranslation('phone');
  const { getDialogProps, getTriggerProps } = usePopoverDialog();

  return (
    <div>
      <Text size='small' color='subdued'>
        {t('Devices')}
      </Text>

      <div
        css={css`
          display: flex;
          align-items: center;
          position: relative;
          bottom: ${theme.spacing(0.5)};
        `}
      >
        <Text size='medium'>{t('{{count}} Devices', { count: deviceNames.length })}</Text>
        {!!deviceNames.length && <Button iconName='preview' variant='secondary' {...getTriggerProps()} />}
      </div>

      <PopoverDialog
        {...getDialogProps()}
        css={css`
          border-radius: ${theme.borderRadius.medium};
          overflow-y: auto;
        `}
      >
        <div
          css={css`
            width: 248px;
            max-height: 368px;
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(0.5)};
          `}
        >
          <Text size='small' color='light'>
            {t('Assigned Devices')}
          </Text>
          <ul
            css={css`
              border-radius: ${theme.borderRadius.medium};
              border: 1px solid ${theme.colors.neutral20};
              padding: 0;

              li:not(:last-child) {
                border-bottom: 1px solid ${theme.colors.neutral20};
              }
            `}
          >
            {deviceNames.map((device) => (
              <li
                key={device}
                css={css`
                  list-style: none;
                  padding: ${theme.spacing(1)};
                `}
              >
                <Text key={device} size='medium'>
                  {device}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      </PopoverDialog>
    </div>
  );
};
